import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { Button, Typography } from 'seagull';
import { AnchorLogo } from 'seagull/icons/color';
import { InvoicePreviewSkeleton } from '../NotificationSkeletons/InvoicePreviewSkeleton';
import { PayoutPreviewSkeleton } from '../NotificationSkeletons/PayoutPreviewSkeleton';
import styles from './NotificationEmailPreview.module.scss';
import { GetNotificationEmailPreviewTID } from './NotificationEmailPreview.testIds';
import { pickSkeleton, resolveSegments } from './NotificationEmailPreview.utils';
export function NotificationEmailPreview(param) {
    let { notificationName, subject, message, templateVariables, customTemplateVariables, ctaButtonText, branding, testId = 'notification-email-preview' } = param;
    const { NotificationEmailPreviewTestIds: testIds } = useMemo(()=>GetNotificationEmailPreviewTID(testId), [
        testId
    ]);
    const subjectSegments = useMemo(()=>resolveSegments(subject, templateVariables, customTemplateVariables), [
        subject,
        templateVariables,
        customTemplateVariables
    ]);
    const paragraphs = useMemo(()=>{
        const parts = message.split('\n');
        return parts.map((paragraph)=>resolveSegments(paragraph, templateVariables, customTemplateVariables));
    }, [
        message,
        templateVariables,
        customTemplateVariables
    ]);
    const skeleton = pickSkeleton(notificationName);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                as: "div",
                variant: "paragraph3",
                weight: "normal",
                className: styles.subjectBar,
                testId: testIds.Subject(),
                children: /*#__PURE__*/ _jsx(SegmentedText, {
                    segments: subjectSegments,
                    unresolvedTestId: testIds.UnresolvedTag
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.emailCard,
                children: [
                    branding.type === 'anchor' ? /*#__PURE__*/ _jsx(AnchorLogo, {
                        className: styles.logo,
                        "aria-hidden": true,
                        "data-testid": testIds.AnchorLogo()
                    }) : branding.logoUrl != null ? /*#__PURE__*/ _jsx("img", {
                        src: branding.logoUrl,
                        alt: "",
                        className: styles.logo,
                        "data-testid": testIds.SenderLogo()
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        variant: "h6",
                        weight: "bold",
                        className: styles.senderName,
                        testId: testIds.SenderName(),
                        children: branding.name
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.body,
                        "data-testid": testIds.Body(),
                        children: [
                            paragraphs.map((segments, index)=>/*#__PURE__*/ _jsx(Typography, {
                                    as: "p",
                                    variant: "paragraph2",
                                    weight: "light",
                                    className: styles.paragraph,
                                    testId: testIds.Paragraph(index),
                                    children: /*#__PURE__*/ _jsx(SegmentedText, {
                                        segments: segments,
                                        unresolvedTestId: testIds.UnresolvedTag
                                    })
                                }, index)),
                            skeleton === 'invoice' && /*#__PURE__*/ _jsx(InvoicePreviewSkeleton, {
                                ctaButtonText: ctaButtonText,
                                testId: testIds.SkeletonInvoice()
                            }),
                            skeleton === 'payout' && /*#__PURE__*/ _jsx(PayoutPreviewSkeleton, {
                                ctaButtonText: ctaButtonText,
                                testId: testIds.SkeletonPayout()
                            })
                        ]
                    }),
                    skeleton == null && ctaButtonText !== '' && /*#__PURE__*/ _jsx("div", {
                        className: styles.ctaWrapper,
                        children: /*#__PURE__*/ _jsx(Button, {
                            className: styles.cta,
                            size: "small",
                            skin: "primary",
                            disabled: true,
                            noBi: true,
                            testId: testIds.CtaButton(),
                            children: ctaButtonText
                        })
                    })
                ]
            })
        ]
    });
}
function SegmentedText(param) {
    let { segments, unresolvedTestId } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: segments.map((segment, index)=>segment.kind === 'text' ? /*#__PURE__*/ _jsx(Fragment, {
                children: segment.value
            }, index) : /*#__PURE__*/ _jsx("span", {
                className: styles.unresolved,
                "data-testid": unresolvedTestId(segment.label),
                children: "{{".concat(segment.label, "}}")
            }, index))
    });
}
